import { Asset, AssetRateCard, BidVersion, ProjectSetup, RateCard, Shot } from "../types";
import { getEpisodeFromShotCode } from "../utils";
import { isBidItemIncluded } from "./bidStatus";

export type ExportScopeType = "full" | "subset";

export interface ExportFilterState {
  isActive: boolean;
  filteredIds: string[];
  search: string;
  advancedFilterCount: number;
  visibleCount: number;
  totalCount: number;
}

export interface ExportScopeSnapshot {
  scopeType: ExportScopeType;
  shotFilter?: ExportFilterState;
  assetFilter?: ExportFilterState;
  onsetDays: number;
}

export interface VersionEpisodeBreakdown {
  episode: string;
  shotCount: number;
  totalValue: number;
}

export interface SnapshotTotals {
  shotTotal: number;
  assetTotal: number;
  onsetTotal: number;
  grandTotal: number;
}

export function padVersionNumber(value: number) {
  return `v${String(value).padStart(2, "0")}`;
}

function formatIsoDateLocal(value?: string | Date | null) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatBidVersionDisplayName({
  versionCode,
  projectName,
  bidStatus,
  companyName = "Vine FX",
  date,
}: {
  versionCode: string;
  projectName?: string | null;
  bidStatus?: string | null;
  companyName?: string | null;
  date?: string | Date | null;
}) {
  const isoDate = formatIsoDateLocal(date);
  const cleanCompany = String(companyName || "Vine FX").trim() || "Vine FX";
  const cleanProject = String(projectName || "Untitled Project").trim() || "Untitled Project";
  const cleanStatus = String(bidStatus || "").trim();
  const cleanVersion = /^v\d+$/i.test(String(versionCode || "").trim())
    ? String(versionCode).trim()
    : padVersionNumber(Number.parseInt(String(versionCode || "").replace(/^v/i, ""), 10) || 1);

  const trailing = cleanStatus ? `${cleanStatus} ${cleanVersion}` : cleanVersion;
  return [isoDate, cleanCompany, cleanProject, trailing].filter(Boolean).join(" - ");
}

export function getNextVersionCode(existingVersions: Pick<BidVersion, "versionCode" | "versionName">[] = []) {
  const versionNumbers = existingVersions
    .map((version) => version.versionCode || version.versionName)
    .map((code) => {
      const match = code?.match(/^v(\d+)$/i);
      return match ? Number.parseInt(match[1], 10) : 0;
    })
    .filter((value) => Number.isFinite(value) && value > 0);

  const nextNumber = versionNumbers.length > 0 ? Math.max(...versionNumbers) + 1 : 1;
  return padVersionNumber(nextNumber);
}

export function getCurrentWorkingVersionCode(
  existingVersions: Pick<BidVersion, "versionCode" | "versionName">[] = [],
  currentVersion?: string | null
) {
  const currentMatch = currentVersion?.match(/^v(\d+)$/i);
  if (currentMatch) {
    return padVersionNumber(Number.parseInt(currentMatch[1], 10));
  }

  return getNextVersionCode(existingVersions);
}

export function calculateShotValue(shot: Shot, rates: RateCard, setup: ProjectSetup) {
  const baseTaskCost =
    (shot.days.concept || 0) * (rates.concept || 0) +
    (shot.days.dmp || 0) * (rates.dmp || 0) +
    (shot.days.comp || 0) * (rates.comp || 0) +
    (shot.days.roto || 0) * (rates.roto || 0) +
    (shot.days.prep || 0) * (rates.prep || 0) +
    (shot.days.gfx || 0) * (rates.gfx || 0) +
    (shot.days.cameraTR || 0) * (rates.cameraTR || 0) +
    (shot.days.objectTR || 0) * (rates.objectTR || 0) +
    (shot.days.rANIM || 0) * (rates.rANIM || 0) +
    (shot.days.layout || 0) * (rates.layout || 0) +
    (shot.days.anim || 0) * (rates.anim || 0) +
    (shot.days.light || 0) * (rates.light || 0) +
    (shot.days.fx || 0) * (rates.fx || 0);

  const taskDays =
    (shot.days.concept || 0) +
    (shot.days.dmp || 0) +
    (shot.days.comp || 0) +
    (shot.days.roto || 0) +
    (shot.days.prep || 0) +
    (shot.days.gfx || 0) +
    (shot.days.cameraTR || 0) +
    (shot.days.objectTR || 0) +
    (shot.days.rANIM || 0) +
    (shot.days.layout || 0) +
    (shot.days.anim || 0) +
    (shot.days.light || 0) +
    (shot.days.fx || 0);

  const supervisionDays = taskDays * ((setup.supervisionPercentage || 0) / 100);
  const supervisionCost = supervisionDays * (rates.supervisor || 0);
  return (baseTaskCost + supervisionCost) * (shot.quantity || 1);
}

export function calculateAssetValue(asset: Asset, assetRates: AssetRateCard, setup: ProjectSetup) {
  const baseTaskCost =
    (asset.days.concept || 0) * (assetRates.concept || 0) +
    (asset.days.dmp || 0) * (assetRates.dmp || 0) +
    (asset.days.model || 0) * (assetRates.model || 0) +
    (asset.days.rig || 0) * (assetRates.rig || 0) +
    (asset.days.retop || 0) * (assetRates.retop || 0) +
    (asset.days.cloth || 0) * (assetRates.cloth || 0) +
    (asset.days.fur || 0) * (assetRates.fur || 0) +
    (asset.days.groom || 0) * (assetRates.groom || 0) +
    (asset.days.texture || 0) * (assetRates.texture || 0) +
    (asset.days.anim || 0) * (assetRates.anim || 0) +
    (asset.days.fx || 0) * (assetRates.fx || 0) +
    (asset.days.lookdev || 0) * (assetRates.lookdev || 0) +
    (asset.days.photoscan || 0) * (assetRates.photoscan || 0);

  const taskDays =
    (asset.days.concept || 0) +
    (asset.days.dmp || 0) +
    (asset.days.model || 0) +
    (asset.days.rig || 0) +
    (asset.days.retop || 0) +
    (asset.days.cloth || 0) +
    (asset.days.fur || 0) +
    (asset.days.groom || 0) +
    (asset.days.texture || 0) +
    (asset.days.anim || 0) +
    (asset.days.fx || 0) +
    (asset.days.lookdev || 0) +
    (asset.days.photoscan || 0);

  const supervisionDays = taskDays * ((setup.supervisionPercentage || 0) / 100);
  const supervisionCost = supervisionDays * (assetRates.supervisor || 0);
  return (baseTaskCost + supervisionCost) * (asset.quantity || 1);
}

export function calculateSnapshotTotals(
  shots: Shot[],
  assets: Asset[],
  setup: ProjectSetup,
  rates: RateCard,
  assetRates: AssetRateCard
): SnapshotTotals {
  const activeShots = shots.filter((shot) => isBidItemIncluded(shot));
  const activeAssets = assets.filter((asset) => isBidItemIncluded(asset));
  const shotTotal = activeShots.reduce((total, shot) => total + calculateShotValue(shot, rates, setup), 0);
  const assetTotal = activeAssets.reduce((total, asset) => total + calculateAssetValue(asset, assetRates, setup), 0);
  const onsetTotal = (setup.onsetSupervision || 0) * (rates.onsetSupervision || 0);

  return {
    shotTotal,
    assetTotal,
    onsetTotal,
    grandTotal: shotTotal + assetTotal + onsetTotal,
  };
}

export function isSubsetExportActive(
  shotFilter: ExportFilterState | null | undefined,
  assetFilter: ExportFilterState | null | undefined
) {
  return Boolean(shotFilter?.isActive || assetFilter?.isActive);
}

export function buildEpisodeBreakdown(
  shots: Shot[],
  setup: ProjectSetup,
  rates: RateCard
): VersionEpisodeBreakdown[] {
  const totals = new Map<string, VersionEpisodeBreakdown>();

  shots
    .filter((shot) => isBidItemIncluded(shot))
    .forEach((shot) => {
      const episode = getEpisodeFromShotCode(shot.shotCode) || "Unassigned";
      const current = totals.get(episode) || { episode, shotCount: 0, totalValue: 0 };
      current.shotCount += 1;
      current.totalValue += calculateShotValue(shot, rates, setup);
      totals.set(episode, current);
    });

  return [...totals.values()].sort((a, b) => a.episode.localeCompare(b.episode));
}

export function getVersionCounts(version: Pick<BidVersion, "shots" | "assets" | "onsetDays">) {
  return {
    shotCount: (version.shots || []).filter((shot) => isBidItemIncluded(shot)).length,
    assetCount: (version.assets || []).filter((asset) => isBidItemIncluded(asset)).length,
    onsetDays: version.onsetDays || 0,
  };
}

export function getCommercialSummaryMetrics(
  shots: Shot[],
  assets: Asset[],
  setup: ProjectSetup,
  rates: RateCard,
  assetRates: AssetRateCard,
  purchaseOrders: { remainingAmount?: number; status?: string }[] = []
) {
  const sumShots = (predicate: (shot: Shot) => boolean, useOriginalAwarded = false) =>
    shots.reduce((total, shot) => {
      if (!predicate(shot)) return total;
      if (useOriginalAwarded && shot.originalAwardedValue !== undefined) {
        return total + shot.originalAwardedValue;
      }
      return total + calculateShotValue(shot, rates, setup);
    }, 0);

  const sumAssets = (predicate: (asset: Asset) => boolean, useOriginalAwarded = false) =>
    assets.reduce((total, asset) => {
      if (!predicate(asset)) return total;
      if (useOriginalAwarded && asset.originalAwardedValue !== undefined) {
        return total + asset.originalAwardedValue;
      }
      return total + calculateAssetValue(asset, assetRates, setup);
    }, 0);

  const totalAwardedToDate =
    sumShots((shot) => ["Awarded", "Rebid", "Overage", "Invoiced"].includes(shot.status || ""), true) +
    sumAssets((asset) => ["Awarded", "Rebid", "Overage", "Invoiced"].includes(asset.status || ""), true);

  const openBiddingTotal =
    sumShots((shot) => ["Bidding", "Rebid"].includes(shot.status || "Bidding")) +
    sumAssets((asset) => ["Bidding", "Rebid"].includes(asset.status || "Bidding"));

  const totalLost =
    sumShots((shot) => shot.status === "Lost") +
    sumAssets((asset) => asset.status === "Lost");

  const totalOverageValue =
    sumShots((shot) => shot.status === "Overage") +
    sumAssets((asset) => asset.status === "Overage");

  const poBalanceRemaining = purchaseOrders
    .filter((po) => po.status !== "Cancelled")
    .reduce((total, po) => total + (po.remainingAmount || 0), 0);

  return {
    totalAwardedToDate,
    openBiddingTotal,
    totalLost,
    totalOverageValue,
    poBalanceRemaining,
  };
}
